/**
 * ============================================================================
 * FEEDBACK SYSTEM (Global Toast/Notification System)
 * ============================================================================
 *
 * Questo sistema fornisce un meccanismo globale per mostrare notifiche
 * (toast) da qualsiasi punto dell’applicazione tramite un Context React.
 *
 * Caratteristiche principali:
 *
 * - API semplice: useFeedbackSystem().show(...)
 * - Supporto a severità: success | info | warning | error
 * - Posizionamento configurabile (top/bottom + left/center/right)
 * - Stack verticale automatico (nessuna sovrapposizione)
 * - Auto-hide configurabile
 * - Animazione di uscita
 * - Compatibile con React 18 (nessun uso di ReactDOM.render)
 *
 * Architettura:
 *
 * - Il Provider mantiene uno stato con la lista dei feedback attivi.
 * - I feedback vengono raggruppati per posizione.
 * - Per ogni posizione viene creato un container fixed.
 * - I toast vengono impilati verticalmente tramite flexbox.
 * - Ogni toast gestisce il proprio timer di chiusura.
 *
 * Non viene utilizzato MUI Snackbar per evitare problemi di
 * posizionamento fixed per-elemento e sovrapposizione.
 * ============================================================================ */

import { Alert, Collapse, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

/* ============================================================================
   Types
============================================================================ */

type FeedbackSeverity = "success" | "info" | "warning" | "error";

type FeedbackPositionType = {
  vertical: "bottom" | "top";
  horizontal: "center" | "left" | "right";
};

type FeedbackSystemType = {
  show: (obj: {
    type: FeedbackSeverity;
    message: string | React.ReactElement;
    feedbackPosition?: FeedbackPositionType;
    isBig?: boolean;
    autoHideDuration?: number;
  }) => void;
};

type FeedbackItem = {
  id: number;
  type: FeedbackSeverity;
  message: string | React.ReactElement;
  feedbackPosition: FeedbackPositionType;
  isBig?: boolean;
  autoHideDuration: number;
};

type FeedbackProps = {
  type: FeedbackSeverity;
  message: string | React.ReactElement;
  isBig?: boolean;
  autoHideDuration: number;
  onClose: () => void;
};

/* ============================================================================
   Constants
============================================================================ */

const defaultPosition: FeedbackPositionType = {
  vertical: "bottom",
  horizontal: "center",
};

/* ============================================================================
   Context
============================================================================ */

const feedbackContext = createContext<FeedbackSystemType>(undefined!);

/* ============================================================================
   Provider
============================================================================ */

export const ProvideFeedbackSystem = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  /**
   * Stato centrale dei feedback attivi.
   * Ogni elemento rappresenta un toast visibile.
   */
  const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([]);

  /**
   * Rimuove un feedback dallo stato (invocato dopo animazione di uscita).
   */
  const remove = (id: number) => {
    setFeedbacks((prev) => prev.filter((f) => f.id !== id));
  };

  /**
   * Metodo pubblico esposto dal Context.
   * Aggiunge un nuovo feedback allo stato.
   */
  const show: FeedbackSystemType["show"] = ({
    type,
    message,
    feedbackPosition = defaultPosition,
    isBig,
    autoHideDuration = 2000,
  }) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);

    setFeedbacks((prev) => [
      ...prev,
      { id, type, message, feedbackPosition, isBig, autoHideDuration },
    ]);

    /**
     * Logging coerente con la severità.
     * Utile per debugging centralizzato.
     */
    switch (type) {
      case "error":
        console.error(message);
        break;
      case "info":
        console.info(message);
        break;
      case "success":
        console.log(message);
        break;
      case "warning":
        console.warn(message);
        break;
    }
  };

  /**
   * Raggruppamento per posizione.
   * Ogni chiave rappresenta un container di stacking indipendente.
   */
  const grouped = useMemo(() => {
    const map = new Map<string, FeedbackItem[]>();

    for (const f of feedbacks) {
      const key = `${f.feedbackPosition.vertical}-${f.feedbackPosition.horizontal}`;
      const arr = map.get(key) ?? [];
      arr.push(f);
      map.set(key, arr);
    }

    return map;
  }, [feedbacks]);

  const value = useMemo(() => ({ show }), []);

  return (
    <feedbackContext.Provider value={value}>
      {children}

      {/**
       * Portal globale verso document.body.
       * I toast vengono renderizzati fuori dall'albero normale
       * per evitare problemi di overflow/z-index.
       */}
      {createPortal(
        <>
          {[...grouped.entries()].map(([key, items]) => {
            const [vertical, horizontal] = key.split(
              "-"
            ) as [
              FeedbackPositionType["vertical"],
              FeedbackPositionType["horizontal"]
            ];

            return (
              <StackContainer
                key={key}
                vertical={vertical}
                horizontal={horizontal}
              >
                {items.map((f) => (
                  <Feedback
                    key={f.id}
                    type={f.type}
                    message={f.message}
                    isBig={f.isBig}
                    autoHideDuration={f.autoHideDuration}
                    onClose={() => remove(f.id)}
                  />
                ))}
              </StackContainer>
            );
          })}
        </>,
        document.body
      )}
    </feedbackContext.Provider>
  );
};

export const useFeedbackSystem = () => useContext(feedbackContext);

/* ============================================================================
   Stack Container
============================================================================ */

/**
 * Container fixed per una specifica posizione.
 * Gestisce:
 * - stacking verticale
 * - direzione (top vs bottom)
 * - offset dal bordo
 * - isolamento dei pointer events
 */
const StackContainer = ({
  children,
  vertical,
  horizontal,
}: {
  children: React.ReactNode;
  vertical: "top" | "bottom";
  horizontal: "left" | "center" | "right";
}) => {
  return (
    <div
      style={{
        position: "fixed",
        zIndex: 1400,
        display: "flex",

        /**
         * Se vertical === bottom usiamo column-reverse
         * così il toast più recente appare vicino al bordo.
         */
        flexDirection: vertical === "top" ? "column" : "column-reverse",

        gap: 8,

        /**
         * Evita che il layer blocchi click sull'app.
         * I singoli toast riabilitano pointerEvents.
         */
        pointerEvents: "none",

        [vertical]: 16,
        ...(horizontal === "center"
          ? { left: "50%", transform: "translateX(-50%)" }
          : { [horizontal]: 16 }),
      }}
    >
      {children}
    </div>
  );
};

/* ============================================================================
   Single Feedback
============================================================================ */

/**
 * Singolo toast.
 * Gestisce:
 * - stato open/close
 * - timer auto-hide
 * - animazione Collapse
 * - chiamata onClose finale al Provider
 */
const Feedback = ({
  type,
  message,
  isBig = false,
  autoHideDuration,
  onClose,
}: FeedbackProps) => {
  const [open, setOpen] = useState(true);
  const closingRef = useRef(false);

  /**
   * Previene doppia chiusura.
   */
  const close = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setOpen(false);
  };

  /**
   * Timer automatico di chiusura.
   */
  useEffect(() => {
    const t = window.setTimeout(() => close(), autoHideDuration);
    return () => window.clearTimeout(t);
  }, [autoHideDuration]);

  return (
    <Collapse
      in={open}
      timeout={200}
      onExited={() => {
        // Notifica al Provider che il toast può essere rimosso dallo stato
        onClose();
      }}
    >
      <div style={{ pointerEvents: "auto" }}>
        <Alert
          severity={type}
          sx={{
            width: "100%",
            ...(isBig
              ? {
                  alignItems: "baseline",
                  maxHeight: 300,
                  overflow: "auto",
                }
              : null),
          }}
          action={
            <IconButton
              aria-label="close"
              size="small"
              onClick={() => close()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {message}
        </Alert>
      </div>
    </Collapse>
  );
};